import {NodeType} from 'crqlar-react-common/dist/models/automation/automation';
import {useTranslation} from 'react-i18next';

import NodeEditorLayout from './NodeEditorLayout';
import AudienceEditorV2 from './actions/AudienceEditorV2';
import AudienceModePicker, {AudienceChoice} from './actions/AudienceModePicker';
import FullSyncAudienceEditorV2 from './actions/FullSyncAudienceEditorV2';
import ContinueIfEditorV2 from './logic/ContinueIfEditorV2';
import IfConditionEditorV2 from './logic/IfConditionEditorV2';
import SendEmailEditorV2 from './actions/SendEmailEditorV2';
import SendEmailIdentityAside from './actions/SendEmailIdentityAside';
import SendPushEditorV2 from './actions/SendPushEditorV2';
import SurveyInviteEditorV2 from './actions/SurveyInviteEditorV2';
import SurveyReportEditorV2 from './actions/SurveyReportEditorV2';
import SendTeamEmailEditorV2 from './actions/SendTeamEmailEditorV2';
import SendTeamEmailIdentityAside from './actions/SendTeamEmailIdentityAside';
import TeamMembersEditorV2 from './actions/TeamMembersEditorV2';
import WaitEditorV2 from './logic/WaitEditorV2';
import AfterEveryoneEditorV2 from './logic/AfterEveryoneEditorV2';
import FinishEditorV2 from './logic/FinishEditorV2';
import FormSubmittedEditorV2 from './triggers/FormSubmittedEditorV2';
import ReservationCreatedEditorV2 from './triggers/ReservationCreatedEditorV2';
import ReservationStatusChangedEditorV2 from './triggers/ReservationStatusChangedEditorV2';
import ScheduledTriggerEditorV2 from './triggers/ScheduledTriggerEditorV2';
import SurveyReminderDueEditorV2 from './triggers/SurveyReminderDueEditorV2';
import SurveySubmittedEditorV2 from './triggers/SurveySubmittedEditorV2';
import {NodeEditorV2Props} from './types';

/** A stored step whose type this build no longer has (Loop, Get Profile): it never runs. */
const RetiredNodeEditor = () => {
    const {t} = useTranslation();

    return (
        <NodeEditorLayout
            state={'wontRun'}
            summary={t('This kind of step is no longer supported and never runs. Delete it, or create a migrated copy of the automation.')}
        />
    );
};

export const renderNodeEditorV2 = (nodeType: NodeType, props: NodeEditorV2Props) => {
    switch (nodeType) {
        case NodeType.MANUAL_TRIGGER:
            return null; // Only the label is editable; Run for everyone belongs to the canvas card.
        case NodeType.SCHEDULED_TRIGGER:
            return <ScheduledTriggerEditorV2 {...props}/>;
        case NodeType.FORM_SUBMITTED:
            return <FormSubmittedEditorV2 {...props}/>;
        case NodeType.RESERVATION_CREATED:
            return <ReservationCreatedEditorV2 {...props}/>;
        case NodeType.RESERVATION_STATUS_CHANGED:
            return <ReservationStatusChangedEditorV2 {...props}/>;
        case NodeType.SURVEY_SUBMITTED:
            return <SurveySubmittedEditorV2 {...props}/>;
        case NodeType.SURVEY_REMINDER_DUE:
            return <SurveyReminderDueEditorV2 {...props}/>;
        case NodeType.AUDIENCE:
            return <AudienceEditorV2 {...props}/>;
        case NodeType.TEAM_MEMBERS:
            return <TeamMembersEditorV2 {...props}/>;
        case NodeType.SEND_EMAIL:
            return <SendEmailEditorV2 {...props}/>;
        case NodeType.SEND_PUSH:
            return <SendPushEditorV2 {...props}/>;
        case NodeType.SURVEY_INVITE:
            return <SurveyInviteEditorV2 {...props}/>;
        case NodeType.SURVEY_REPORT:
            return <SurveyReportEditorV2 {...props}/>;
        case NodeType.SEND_TEAM_EMAIL:
            return <SendTeamEmailEditorV2 {...props}/>;
        case NodeType.FULL_SYNC_AUDIENCE:
            return <FullSyncAudienceEditorV2 {...props}/>;
        case NodeType.CONTINUE_IF:
            return <ContinueIfEditorV2 {...props}/>;
        case NodeType.IF_CONDITION:
            return <IfConditionEditorV2 {...props}/>;
        case NodeType.WAIT:
            return <WaitEditorV2 {...props}/>;
        case NodeType.AFTER_EVERYONE:
            return <AfterEveryoneEditorV2 {...props}/>;
        case NodeType.FINISH:
            return <FinishEditorV2 {...props}/>;
        default:
            return <RetiredNodeEditor/>;
    }
};

/**
 * Extra controls under the identity panel's copy line, same draft: Send Email's mode and variable mappings, Send team
 * email's recipient and attachments.
 */
export const renderNodeIdentityAsideV2 = (nodeType: NodeType, props: NodeEditorV2Props) => {
    switch (nodeType) {
        case NodeType.SEND_EMAIL:
            return <SendEmailIdentityAside {...props}/>;
        case NodeType.SEND_TEAM_EMAIL:
            return <SendTeamEmailIdentityAside {...props}/>;
        default:
            return null;
    }
};

/** A header control that reshapes the whole editor (Audience: Use/Create); its value is the dialog's `headerChoice`. */
export const renderNodeHeaderAsideV2 = (nodeType: NodeType, props: NodeEditorV2Props) => {
    switch (nodeType) {
        case NodeType.AUDIENCE:
            return (
                <AudienceModePicker
                    value={props.headerChoice}
                    onChange={(choice: AudienceChoice) => props.setHeaderChoice?.(choice)}
                    disabled={props.readOnly}
                />
            );
        default:
            return null;
    }
};
